import {
  reportConversion,
  type ConversionName,
} from "@/engine/diagnostics/adsConsent";

/**
 * The sign-up conversion an ad campaign pays for.
 *
 * A session arrives on every restore, every tab and every token refresh, so
 * "signed in" is not the outcome — "this account came into existence just now"
 * is. Freshness answers that from the account itself, and the local note keeps
 * a second tab or a reload in the same minute from counting it twice.
 */

const STORAGE_KEY = "syncani.signup-reported";
/** How long after creation a session still counts as the sign-up itself. */
const FRESH_ACCOUNT_MS = 10 * 60 * 1000;

const CONVERSION: ConversionName = "account-signed-up";

function alreadyReported(accountId: string): boolean {
  try {
    return localStorage.getItem(`${STORAGE_KEY}:${accountId}`) === "1";
  } catch {
    return false;
  }
}

function note(accountId: string): void {
  try {
    localStorage.setItem(`${STORAGE_KEY}:${accountId}`, "1");
  } catch {
    /* a private-mode tab reports once per visit instead; harmless */
  }
}

export interface SignupAccount {
  id: string;
  email?: string | null;
  /** ISO timestamp of when the account record was created. */
  createdAt?: string | null;
}

/**
 * Reports the sign-up once for a freshly created account. Never throws: an
 * advertising report must not stand between a visitor and their session.
 */
export async function reportAccountSignup(
  account: SignupAccount,
  now: number = Date.now(),
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  if (!account.id || !account.createdAt) return;
  const created = Date.parse(account.createdAt);
  if (!Number.isFinite(created)) return;
  if (now - created > FRESH_ACCOUNT_MS) return;
  if (alreadyReported(account.id)) return;
  note(account.id);
  await reportConversion(CONVERSION, fetchImpl, account.email ?? null);
}
